/**
 * Role normalization helpers (client side).
 *
 * Mirrors `server/utils/roles.js`. The backend exposes a user object with
 * either a denormalized `role` string (legacy) or a populated
 * `roleId: { name }` reference (newer, dynamic roles). Various components
 * used to pick one or the other, which caused drift where migrated users
 * silently lost access. This helper is the single source of truth.
 *
 * Usage:
 *   import { getEffectiveRole, isSuperAdmin } from 'src/utils/roles';
 *   const role = getEffectiveRole(user);
 */

export const ELEVATED_ROLES = new Set(['super_admin', 'admin']);
export const TEAM_VIEW_ROLES = new Set(['super_admin', 'admin', 'team_manager']);

export function getEffectiveRole(user) {
    if (!user) return null;
    const roleIdName = user.roleId && typeof user.roleId === 'object' ? user.roleId.name : null;
    const raw = roleIdName || user.role || null;
    if (!raw) return null;
    // Normalise to snake_case ("Team Manager" -> "team_manager") so every
    // downstream comparison (`user.role === 'team_manager'`) matches
    // regardless of whether the DB stores a human label or the canonical code.
    return String(raw).toLowerCase().trim().replace(/\s+/g, '_');
}

export function isElevatedRole(role) {
    return ELEVATED_ROLES.has(role);
}

export function canViewTeam(role) {
    return TEAM_VIEW_ROLES.has(role);
}

export function isSuperAdmin(userOrRole) {
    const role = typeof userOrRole === 'string' ? userOrRole : getEffectiveRole(userOrRole);
    return role === 'super_admin';
}

const rolesHelpers = { getEffectiveRole, isElevatedRole, canViewTeam, isSuperAdmin, ELEVATED_ROLES, TEAM_VIEW_ROLES };
export default rolesHelpers;
